import { useEffect, useRef } from 'react';
import { isLessonCompleted } from './useTutorialProgress';

// Marcos de progresso enviados à API (o de 90% já conta a lição como assistida)
const MILESTONES = [0.25, 0.5, 0.75, 0.9];
// Pausas e saídas só gravam a posição se ela andou pelo menos isto desde o último envio
const MIN_POSITION_DELTA_SEC = 5;

const finiteSeconds = (value) => (Number.isFinite(value) ? Math.round(value) : 0);

/**
 * Liga os eventos do <video> da lição ao progresso do usuário: abertura, marcos de 25/50/75/90%,
 * posição ao pausar ou sair (para retomar de onde parou) e fim do vídeo.
 * @param {Object} lesson - lição de tutorials.json (id e versão vão junto em cada evento)
 * @param {String} playKey - muda a cada vez que a lição é escolhida (o vídeo recomeça)
 * @param {Object} progress - progresso salvo da lição (opcional)
 * @param {String} source - 'page' (página Tutoriais) ou 'help' (atalho "Como usar esta tela")
 * @param {Function} onTrack - track() de useTutorialProgress; sem ele nada é gravado
 * @returns {Object} handlers para o <video>
 */
const useLessonTracking = ({ lesson, playKey, progress, source, onTrack }) => {
  const sessionRef = useRef(null);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  // Uma "sessão" por vídeo exibido; ao trocar de lição ou fechar, grava onde a pessoa parou
  useEffect(() => {
    const session = { opened: false, resumed: false, milestone: -1, sentPosition: 0, position: 0, duration: 0, ended: false };
    sessionRef.current = session;
    return () => {
      if (onTrack && session.opened && !session.ended
        && Math.abs(session.position - session.sentPosition) >= MIN_POSITION_DELTA_SEC) {
        onTrack(lesson, 'progress', { position_sec: session.position, duration_sec: session.duration });
      }
    };
  }, [lesson, playKey, onTrack]);

  if (!onTrack) return {};

  const sendProgress = (session) => {
    session.sentPosition = session.position;
    onTrack(lesson, 'progress', { position_sec: session.position, duration_sec: session.duration });
  };

  return {
    onLoadedMetadata: (event) => {
      const session = sessionRef.current;
      const saved = progressRef.current;
      const video = event.currentTarget;
      session.duration = finiteSeconds(video.duration);
      // Retoma de onde parou, se não terminou a lição e não estava no comecinho nem no fim
      const resumeAt = saved?.last_position_sec || 0;
      if (!session.resumed && !isLessonCompleted(saved)
        && resumeAt > MIN_POSITION_DELTA_SEC && resumeAt < session.duration - MIN_POSITION_DELTA_SEC) {
        video.currentTime = resumeAt;
        session.sentPosition = resumeAt;
      }
      session.resumed = true;
    },
    onPlay: () => {
      const session = sessionRef.current;
      session.ended = false;
      if (!session.opened) {
        session.opened = true;
        onTrack(lesson, 'open', { source });
      }
    },
    onTimeUpdate: (event) => {
      const session = sessionRef.current;
      const video = event.currentTarget;
      session.position = finiteSeconds(video.currentTime);
      session.duration = finiteSeconds(video.duration) || session.duration;
      if (!session.opened || !session.duration) return;
      const ratio = video.currentTime / video.duration;
      let reached = session.milestone;
      while (reached + 1 < MILESTONES.length && ratio >= MILESTONES[reached + 1]) reached += 1;
      if (reached > session.milestone) {
        session.milestone = reached;
        sendProgress(session);
      }
    },
    onPause: (event) => {
      const session = sessionRef.current;
      // A pausa automática do fim do vídeo fica por conta do onEnded
      if (event.currentTarget.ended || !session.opened) return;
      if (Math.abs(session.position - session.sentPosition) >= MIN_POSITION_DELTA_SEC) sendProgress(session);
    },
    onEnded: () => {
      const session = sessionRef.current;
      session.ended = true;
      onTrack(lesson, 'complete', { duration_sec: session.duration });
    }
  };
};

export default useLessonTracking;
